// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file SideNavCollapseButton.tsx
 * @input Uses React, StyleX, SideNavCollapseContext, getIcon
 * @output Exports SideNavCollapseButton component
 * @position Composable toggle button for sidenav collapse
 *
 * Place inside SideNav (reads context automatically) or outside
 * (pass handleRef to connect). Customizable via label/children.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/SideNav/SideNav.doc.mjs
 * - /packages/core/src/SideNav/index.ts
 * - /packages/cli/assets/templates/blocks/components/SideNav/ (showcase blocks)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { durationVars, easeVars } from "../theme/tokens.stylex.js";
import { useIcon } from "../Icon/index.js";
import { Button } from "../Button/index.js";
import { composeEventHandlers, rtlStyles } from "../utils/index.js";
import { useSideNavCollapse } from "./SideNavCollapseContext.js";
import { useAppShellMobile } from "../AppShell/AppShellMobileContext.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Component
// =============================================================================

/**
 * Composable toggle button for sidenav collapse.
 *
 * Place anywhere inside SideNav (header, topContent, footer, footerIcons)
 * and it reads collapse state from context automatically. For placement
 * outside the sidenav (e.g. in TopNav or content area), pass handleRef.
 *
 * @example
 * ```
 * <SideNav isCollapsible footerIcons={<SideNavCollapseButton />}>
 *   ...
 * </SideNav>
 * ```
 *
 * @example
 * ```
 * const ref = useRef(null);
 * <TopNav endContent={<SideNavCollapseButton handleRef={ref} />} />
 * <SideNav handleRef={ref} collapsible>...</SideNav>
 * ```
 */
export function SideNavCollapseButton({
  ref,
  handleRef,
  label,
  children,
  onClick: onClickProp,
  ...props
}) {
  const t = useTranslator();
  const chevronLeftIcon = useIcon('chevronLeft');
  const {
    isCollapsed,
    toggle,
    isCollapsible
  } = useSideNavCollapseState(handleRef);
  const {
    isMobile
  } = useAppShellMobile();

  // Hide when not collapsible, or when in mobile mode (sidenav is in
  // the mobile drawer — collapse doesn't apply there)
  if (!isCollapsible || isMobile) {
    return null;
  }
  return /*#__PURE__*/_jsx(Button, {
    ref: ref,
    label: label ?? (isCollapsed ? t('@astryx.sideNavCollapseButton.expandSidebar') : t('@astryx.sideNavCollapseButton.collapseSidebar')),
    variant: "ghost",
    ...props,
    onClick: composeEventHandlers(onClickProp, toggle),
    icon: children ?? /*#__PURE__*/_jsx("span", {
      ...stylex.props(rtlStyles.mirror),
      children: /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92"
          },
          1: {
            className: "x3nfvp2 x6s0dn4 x11xpdln xuedmi6 xlr8y92 x19jd1h0"
          }
        }[!!isCollapsed << 0],
        children: chevronLeftIcon
      })
    }),
    isIconOnly: true
  });
}
SideNavCollapseButton.displayName = 'SideNavCollapseButton';
function useSideNavCollapseState(handleRef) {
  const contextCollapseState = useSideNavCollapse();
  if (handleRef == null) {
    return contextCollapseState;
  }
  const externalCollapseState = handleRef.current?.getCollapseState() ?? null;
  return {
    isCollapsed: externalCollapseState?.isCollapsed ?? false,
    toggle: () => {
      handleRef.current?.getCollapseState()?.toggle();
    },
    isCollapsible: externalCollapseState?.isCollapsible ?? true
  };
}